import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Typogaphy } from 'vnetwork-ui'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import type { DataTableAction } from '@/components/ui/data-table-customized'
import { Button } from '@/components/ui/button'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { Plus } from 'lucide-react'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import EditIcons from '@/icons/EditIcons'
import { AddInvoiceItemsProps } from '@/modals/InvoiceModals/AddInvoiceItems'
import { InvoiceStatusTypes } from '@/modules/Customer/types'
import { INVOICE_STATUS_OPTIONS } from '@/modules/Invoice/constants'
import { deleteInvoiceItem, getInvoiceItems } from '@/modules/Invoice/services'
import type { InvoiceDetail, InvoiceItem } from '@/modules/Invoice/types'
import { formatCurrency } from '@/utils/function'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getInvoiceItemColumns } from './columns'
import Paper from '@/components/Paper'

interface ListInvoiceItemProps {
    data: Partial<InvoiceDetail> & {
        invoice_amount?: number
        invoice_vat?: number
        invoice_total?: number
    }
}

const ListInvoiceItem: React.FC<ListInvoiceItemProps> = ({ data }) => {
    const { t } = useTranslation()
    const { id } = useParams()
    const queryClient = useQueryClient()
    const { setConfirmation } = useConfirmation()
    const { setModal } = useModal()

    const invoiceId = Number(id)

    // Fetch invoice items
    const { data: itemsData, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.INVOICE_ITEMS, invoiceId],
        queryFn: () => getInvoiceItems(invoiceId),
    })

    const items = itemsData?.results || []

    const invalidateQueries = () => {
        queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.INVOICE_ITEMS, invoiceId],
        })
        queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.INVOICE_DETAIL, invoiceId],
        })
    }

    const { mutateAsync: deleteInvoiceItemMutation } = useMutationWithHandler(
        { mutationFn: deleteInvoiceItem },
        {
            reactQueryKeys: QUERY_KEYS.INVOICES,
            successMessage: t('delete-invoice-item-success'),
            onSuccess: invalidateQueries,
        }
    )

    const canEdit = [
        INVOICE_STATUS_OPTIONS.WaitingConfirm,
        INVOICE_STATUS_OPTIONS.Approved,
    ].includes(data.invoice_status as InvoiceStatusTypes)

    const currency = useMemo(() => items?.[0]?.currency || 'VND', [items])

    const columns = useMemo(() => getInvoiceItemColumns(t), [t])

    const actions: DataTableAction<InvoiceItem>[] = useMemo(
        () => [
            {
                key: 'edit',
                label: t('edit'),
                icon: <EditIcons className="w-4 h-4" />,
                variant: 'info',
                onClick: (row) =>
                    setModal<AddInvoiceItemsProps>(
                        'InvoiceModals/AddInvoiceItems/index',
                        {
                            invoice_id: invoiceId,
                            customer_id: data?.customer?.customer_id as number,
                            service_id: data?.service_id as number,
                            invoice_item: row,
                        }
                    ),
                disabled: () => !canEdit,
            },
            {
                key: 'delete',
                label: t('delete'),
                icon: <DeleteOutlineIcons className="w-4 h-4" />,
                variant: 'danger',
                onClick: (row) =>
                    setConfirmation({
                        open: true,
                        title: t('confirm'),
                        description: t('delete-invoice-item-description'),
                        onConfirm: () =>
                            deleteInvoiceItemMutation({
                                invoice_item_id: row?.invoice_item_id,
                                invoice_id: invoiceId,
                            }),
                    }),
                disabled: () => !canEdit,
            },
        ],
        [
            t,
            canEdit,
            invoiceId,
            data?.customer?.customer_id,
            data?.service_id,
            setModal,
            setConfirmation,
            deleteInvoiceItemMutation,
        ]
    )

    const canAdd = data.invoice_status === INVOICE_STATUS_OPTIONS.WaitingConfirm

    const addButton = (
        <Button
            variant="outline"
            size="sm"
            disabled={!canAdd}
            onClick={() =>
                setModal<AddInvoiceItemsProps>(
                    'InvoiceModals/AddInvoiceItems/index',
                    {
                        invoice_id: invoiceId,
                        customer_id: data?.customer?.customer_id as number,
                        service_id: data?.service_id as number,
                        invoice_item: undefined,
                    }
                )
            }
            data-tour="action-add-invoice-line"
        >
            <Plus className="w-4 h-4 mr-1" />
            {t('add-invoice-item')}
        </Button>
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <Paper>
            <DataTableCustomized
                columns={columns}
                rows={items}
                actions={actions}
                autoWidth
                resizable
                rowKey={(row) => row.invoice_item_id}
                toolbar={{
                    slotComponents: [addButton],
                }}
            />

            {/* Invoice Summary */}
            <div className="flex justify-end mt-2">
                <div className="w-80 max-w-full">
                    <div>
                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <Typogaphy className="text-gray-600">
                                    {t('subtotal')}:
                                </Typogaphy>
                                <Typogaphy className="font-medium text-gray-900">
                                    {formatCurrency(
                                        data?.invoice_amount || 0,
                                        currency
                                    )}
                                </Typogaphy>
                            </div>

                            <div className="flex justify-between items-center">
                                <Typogaphy className="text-gray-600">
                                    VAT:
                                </Typogaphy>
                                <Typogaphy className="font-medium text-gray-900">
                                    {formatCurrency(
                                        data?.invoice_vat || 0,
                                        currency
                                    )}
                                </Typogaphy>
                            </div>

                            <hr className="border-gray-200" />

                            <div className="flex justify-between items-center">
                                <Typogaphy className="text-gray-900 font-semibold">
                                    {t('total-invoice')}:
                                </Typogaphy>
                                <Typogaphy
                                    size="medium"
                                    className="font-bold text-primary"
                                >
                                    {formatCurrency(
                                        data?.invoice_total || 0,
                                        currency
                                    )}
                                </Typogaphy>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Paper>
    )
}

export default ListInvoiceItem
